/**
 * MedicationReminderCard
 *
 * Displays an active medication reminder as a notification card.
 * Shown at the reminder time and requires user confirmation to dismiss.
 * Design inspired by ActiveSleepTimerCard with pp-teal/50 background.
 *
 * Features:
 * - X-Icon to dismiss with confirmation flow (bottom-sheet modal)
 * - Step indicator (2 bars)
 * - Cancel path: overview of reminder details + confirm cancel
 * - Confirm path: choose "now" or custom time, then logs to Baby-Care
 */

import { useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Pill,
  X,
  Loader2,
  Clock,
  Sun,
  ChevronRight,
  AlertTriangle,
  Check,
} from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import type { MedicationReminder } from '@/services/medicationReminderService';

type ModalStep = 'choice' | 'cancel-confirm' | 'confirm-time' | 'success' | 'adjust-schedule';

const STEP_INDEX: Record<ModalStep, number> = {
  choice: 0,
  'cancel-confirm': 1,
  'confirm-time': 1,
  success: 2,
  'adjust-schedule': 2,
};

interface MedicationReminderCardProps {
  reminder: MedicationReminder;
  babyName: string;
  /** The actual scheduled time from the log entry (may differ from next_reminder_at after cron advances it) */
  scheduledFor?: string;
  /** Called when user confirms taking the medication */
  onConfirm: (reminderId: string, takenAt: string) => Promise<void>;
  /** Called when user dismisses/cancels the reminder */
  onDismiss: (reminderId: string) => void;
  /** Called when user wants to adjust the schedule after late administration */
  onAdjustSchedule?: (reminderId: string, newBaseTime: string) => Promise<void>;
}

export function MedicationReminderCard({
  reminder,
  babyName,
  scheduledFor,
  onConfirm,
  onDismiss,
  onAdjustSchedule,
}: MedicationReminderCardProps) {
  const { t } = useTranslation('babyCare');
  const [showModal, setShowModal] = useState(false);
  const [modalStep, setModalStep] = useState<ModalStep>('choice');
  const [selectedTime, setSelectedTime] = useState('');
  const [isConfirming, setIsConfirming] = useState(false);
  const [confirmedTakenAt, setConfirmedTakenAt] = useState<string | null>(null);
  const [isAdjusting, setIsAdjusting] = useState(false);

  const currentStep = STEP_INDEX[modalStep];

  const displayTime = scheduledFor || reminder.next_reminder_at;
  const reminderTime = displayTime
    ? new Date(displayTime).toLocaleTimeString('de-DE', {
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';

  const openModal = () => {
    setModalStep('choice');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  /**
   * Check if the taken time is significantly later than the scheduled time (> 15 min).
   * If so, offer to adjust the schedule.
   */
  const isLateAdministration = (takenAtIso: string): boolean => {
    if (!displayTime) return false;
    const scheduledDate = new Date(displayTime);
    const takenDate = new Date(takenAtIso);
    const diffMs = takenDate.getTime() - scheduledDate.getTime();
    return diffMs > 15 * 60 * 1000; // more than 15 minutes late
  };

  const finishAfterConfirm = (takenAtIso: string) => {
    if (onAdjustSchedule && isLateAdministration(takenAtIso)) {
      setConfirmedTakenAt(takenAtIso);
      setModalStep('adjust-schedule');
    } else {
      setModalStep('success');
      setTimeout(() => {
        closeModal();
        onDismiss(reminder.id);
      }, 1200);
    }
  };

  const handleConfirmNow = async () => {
    const nowIso = new Date().toISOString();
    setIsConfirming(true);
    await onConfirm(reminder.id, nowIso);
    setIsConfirming(false);
    finishAfterConfirm(nowIso);
  };

  const handleConfirmWithTime = async () => {
    if (!selectedTime) return;

    const today = new Date();
    const [hours, minutes] = selectedTime.split(':').map(Number);
    const takenAt = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      hours,
      minutes
    );

    const takenAtIso = takenAt.toISOString();
    setIsConfirming(true);
    await onConfirm(reminder.id, takenAtIso);
    setIsConfirming(false);
    finishAfterConfirm(takenAtIso);
  };

  const handleAdjustSchedule = async () => {
    if (!onAdjustSchedule || !confirmedTakenAt) return;
    setIsAdjusting(true);
    await onAdjustSchedule(reminder.id, confirmedTakenAt);
    setIsAdjusting(false);
    setModalStep('success');
    setTimeout(() => {
      closeModal();
      onDismiss(reminder.id);
    }, 1200);
  };

  const handleSkipAdjust = () => {
    setModalStep('success');
    setTimeout(() => {
      closeModal();
      onDismiss(reminder.id);
    }, 1200);
  };

  const handleCancel = () => {
    closeModal();
    onDismiss(reminder.id);
  };

  // Step indicator (2 bars for 3 logical steps)
  const stepIndicator = (
    <div className="flex gap-1 px-4 pb-3">
      {Array.from({ length: 2 }, (_, i) => (
        <div
          key={i}
          className={`flex-1 h-1 rounded-full ${
            i < currentStep ? 'bg-pp-orange' : 'bg-white/20'
          }`}
        />
      ))}
    </div>
  );

  return (
    <>
      {/* Medication Reminder Card */}
      <div className="bg-pp-teal/50 rounded-[15px] p-4 relative overflow-hidden">
        {/* Decorative Pill icon */}
        <div className="absolute -left-6 top-1/2 -translate-y-1/2 pointer-events-none">
          <div className="w-[140px] h-[140px] rounded-full bg-white/10 flex items-center justify-center">
            <Pill className="w-16 h-16 text-pp-dark/25" />
          </div>
        </div>

        {/* Content */}
        <div className="relative z-10">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <p className="text-white text-2xl font-bold truncate">
                {reminder.medication_name}
              </p>
              <p className="text-white/70 text-sm mt-0.5">
                {reminder.medication_dose && `${reminder.medication_dose} · `}
                {reminderTime}
                {babyName && babyName !== 'Baby' && ` · ${babyName}`}
              </p>
            </div>
            <button
              onClick={openModal}
              className="w-10 h-10 bg-pp-orange text-white rounded-[10px] flex items-center justify-center hover:bg-pp-orange/90 transition-colors flex-shrink-0 ml-3"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Multi-step Bottom Sheet Modal */}
      {showModal &&
        createPortal(
          <>
            <div className="bottom-sheet-backdrop" onClick={closeModal} />
            <div className="bottom-sheet-container">
              <div className="bottom-sheet-modal">
                {/* ── Step 1: Choice (Abbrechen / Bestätigen) ── */}
                {modalStep === 'choice' && (
                  <>
                    <div className="bottom-sheet-header">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center">
                          <Pill className="w-5 h-5 text-red-400" />
                        </div>
                        <h2>{reminder.medication_name}</h2>
                      </div>
                      <button
                        onClick={closeModal}
                        className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>

                    {stepIndicator}

                    <div className="bottom-sheet-content space-y-3">
                      {/* Confirm option */}
                      <button
                        onClick={() => {
                          const now = new Date();
                          setSelectedTime(
                            now.toTimeString().slice(0, 5)
                          );
                          setModalStep('confirm-time');
                        }}
                        className="w-full flex items-center justify-between p-4 glass-effect rounded-[15px] hover:bg-white/15 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <Check className="w-5 h-5 text-green-400" />
                          <div className="text-left">
                            <span className="text-white font-medium block">
                              {t('medicationReminder.confirm')}
                            </span>
                            <span className="text-white/50 text-sm">
                              {t('medicationReminder.confirmDesc')}
                            </span>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-white/60 flex-shrink-0" />
                      </button>

                      {/* Cancel option */}
                      <button
                        onClick={() => setModalStep('cancel-confirm')}
                        className="w-full flex items-center justify-between p-4 glass-effect rounded-[15px] hover:bg-white/15 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <X className="w-5 h-5 text-red-400" />
                          <div className="text-left">
                            <span className="text-white font-medium block">
                              {t('medicationReminder.cancelAction')}
                            </span>
                            <span className="text-white/50 text-sm">
                              {t('medicationReminder.cancelDesc')}
                            </span>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-white/60 flex-shrink-0" />
                      </button>
                    </div>

                    <div className="bottom-sheet-footer" />
                  </>
                )}

                {/* ── Step 2a: Cancel Confirmation ── */}
                {modalStep === 'cancel-confirm' && (
                  <>
                    <div className="bottom-sheet-header">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center">
                          <AlertTriangle className="w-5 h-5 text-red-400" />
                        </div>
                        <h2>{t('medicationReminder.cancelConfirmTitle')}</h2>
                      </div>
                      <button
                        onClick={closeModal}
                        className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>

                    {stepIndicator}

                    <div className="bottom-sheet-content">
                      <div className="p-4 glass-effect rounded-[15px] space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-white/60">
                            {t('medicationReminder.medication')}
                          </span>
                          <span className="text-white font-medium">
                            {reminder.medication_name}
                          </span>
                        </div>
                        {reminder.medication_dose && (
                          <div className="flex justify-between text-sm">
                            <span className="text-white/60">
                              {t('medicationReminder.dosage')}
                            </span>
                            <span className="text-white font-medium">
                              {reminder.medication_dose}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between text-sm">
                          <span className="text-white/60">
                            {t('medicationReminder.time')}
                          </span>
                          <span className="text-white font-medium">
                            {reminderTime}
                          </span>
                        </div>
                        {reminder.notes && (
                          <div className="flex justify-between text-sm">
                            <span className="text-white/60">
                              {t('medicationReminder.note')}
                            </span>
                            <span className="text-white font-medium truncate max-w-[60%]">
                              {reminder.notes}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="bottom-sheet-footer">
                      <button
                        onClick={() => setModalStep('choice')}
                        className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30"
                      >
                        {t('sleepTimer.back')}
                      </button>
                      <button
                        onClick={handleCancel}
                        className="flex-1 py-2.5 rounded-xl bg-red-500 text-white font-medium hover:bg-red-600"
                      >
                        {t('medicationReminder.cancelConfirm')}
                      </button>
                    </div>
                  </>
                )}

                {/* ── Step 2b: Confirm Time (Jetzt / Zeit wählen) ── */}
                {modalStep === 'confirm-time' && (
                  <>
                    <div className="bottom-sheet-header">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                          <Check className="w-5 h-5 text-green-400" />
                        </div>
                        <h2>{t('medicationReminder.whenTaken')}</h2>
                      </div>
                      <button
                        onClick={closeModal}
                        className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>

                    {stepIndicator}

                    <div className="bottom-sheet-content space-y-3">
                      {/* Now Option */}
                      <button
                        onClick={handleConfirmNow}
                        disabled={isConfirming}
                        className="w-full flex items-center justify-between p-4 glass-effect rounded-[15px] hover:bg-white/15 transition-colors disabled:opacity-50"
                      >
                        <div className="flex items-center gap-3">
                          <Sun className="w-5 h-5 text-pp-orange" />
                          <span className="text-white font-medium">
                            {t('sleepTimer.now')}
                          </span>
                        </div>
                        {isConfirming ? (
                          <Loader2 className="w-5 h-5 text-white/60 animate-spin" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-white/60" />
                        )}
                      </button>

                      {/* Custom Time */}
                      <div className="p-4 glass-effect rounded-[15px] space-y-3">
                        <div className="flex items-center gap-3">
                          <Clock className="w-5 h-5 text-indigo-400" />
                          <span className="text-white font-medium">
                            {t('sleepTimer.chooseTime')}
                          </span>
                        </div>
                        <input
                          type="time"
                          value={selectedTime}
                          onChange={(e) => setSelectedTime(e.target.value)}
                          className="w-full p-3 bg-white/10 rounded-[10px] text-white text-center text-xl font-mono border border-white/20 focus:border-pp-orange focus:outline-none"
                        />
                        <button
                          onClick={handleConfirmWithTime}
                          disabled={isConfirming || !selectedTime}
                          className="w-full py-2.5 rounded-xl bg-pp-orange text-white font-medium hover:bg-pp-orange/90 disabled:opacity-50 flex items-center justify-center"
                        >
                          {isConfirming ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                          ) : (
                            t('sleepTimer.confirm')
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="bottom-sheet-footer">
                      <button
                        onClick={() => setModalStep('choice')}
                        className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30"
                      >
                        {t('sleepTimer.back')}
                      </button>
                    </div>
                  </>
                )}

                {/* ── Adjust Schedule ── */}
                {modalStep === 'adjust-schedule' && (
                  <>
                    <div className="bottom-sheet-header">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-pp-orange/20 rounded-full flex items-center justify-center">
                          <Clock className="w-5 h-5 text-pp-orange" />
                        </div>
                        <h2>{t('medicationReminder.adjustTitle')}</h2>
                      </div>
                      <button
                        onClick={handleSkipAdjust}
                        className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>

                    {stepIndicator}

                    <div className="bottom-sheet-content space-y-3">
                      <p className="text-white/70 text-sm text-center">
                        {t('medicationReminder.adjustDescription')}
                      </p>
                    </div>

                    <div className="bottom-sheet-footer">
                      <button
                        onClick={handleSkipAdjust}
                        className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30"
                      >
                        {t('medicationReminder.adjustKeep')}
                      </button>
                      <button
                        onClick={handleAdjustSchedule}
                        disabled={isAdjusting}
                        className="flex-1 py-2.5 rounded-xl bg-pp-orange text-white font-medium hover:bg-pp-orange/90 disabled:opacity-50 flex items-center justify-center"
                      >
                        {isAdjusting ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          t('medicationReminder.adjustYes')
                        )}
                      </button>
                    </div>
                  </>
                )}

                {/* ── Success ── */}
                {modalStep === 'success' && (
                  <div className="bottom-sheet-content">
                    <div className="flex flex-col items-center py-8">
                      <div className="w-16 h-16 rounded-full bg-green-500 flex items-center justify-center mb-4">
                        <Check className="w-8 h-8 text-white" />
                      </div>
                      <p className="text-white text-lg font-medium">
                        {t('medicationPlan.saved')}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>,
          document.body
        )}
    </>
  );
}
